import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Spinner, UncontrolledTooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { paginateStates } from 'redux/actions/stateActions';

import { ReactComponent as TagIcon } from 'assets/icon-tag.svg';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';

import { colors } from 'shared/defaultTheme';
import { ButtonIcon } from 'shared/components/Buttons';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import Pagination from 'shared/components/Pagination';

import { StateColor, TdActions } from './styles';

const PAGE_SIZE = 10;

const StatesTable = ({
  loading,
  setCurrentState,
  setOpenFormModal,
  handleRefresh,
  urlFragment,
}) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const theme = useSelector((state) => state.theme);
  const documentState = useSelector((state) => state.documentState);
  const { mainColor } = theme;
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [SelectedRemoveState, setSelectedRemoveState] = useState(null);
  const [showConfirmRemoveState, setShowConfirmRemoveState] = useState(false);
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const { states, currentStates } = documentState;
  const stateCellRef = useRef(null);
  const [{ loading: lodingDelete }, deleteState] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = (currentState) => {
    setCurrentState(currentState);
    setOpenFormModal(true);
  };

  const onRemove = (currentNotification) => {
    setSelectedRemoveState(currentNotification);
    setShowConfirmRemoveState(true);
  };

  const onOkSuccessRemove = () => {
    setSelectedRemoveState(null);
    onToggleSuccessRemove();
    handleRefresh();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveState(false);
    setSelectedRemoveState(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveState !== undefined && SelectedRemoveState !== null) {
        setShowConfirmRemoveState(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, id } = SelectedRemoveState;
      if (lodingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteState({
        url: `${window.API_V1}/${urlFragment}/${clientNavisionId}/${id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveState(false);
      onToggleErrorRemove();
    }
  };

  const onFirstPage = () => {
    dispatch(paginateStates({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateStates({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateStates({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateStates({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateStates({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(states.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [states]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    if (!loading) {
      let maxWidth = 0;
      currentStates.forEach((state) => {
        const cell = document.getElementById(`tooltip-${state.id}`);
        const cellWidth = cell.offsetWidth;
        maxWidth = Math.max(maxWidth, cellWidth) + 5;
      });
      currentStates.forEach((state) => {
        const cell = document.getElementById(`tooltip-${state.id}`);
        cell.style.width = `${maxWidth}px`;
      });
    }
  }, [currentStates]);

  return (
    <div className="control-panel__table">
      {!loading ? (
        <Table>
          <thead>
            <tr>
              <th className="text-uppercase">{t('general_states')}</th>
            </tr>
          </thead>
          <tbody>
            {currentStates.map((state) => (
              <tr key={state.id}>
                <td
                  aria-hidden="true"
                  onClick={() => onEdit(state)}
                  className="link-name-state"
                  ref={stateCellRef}
                >
                  <StateColor id={`tooltip-${state.id}`} color={state.color} className="state-cell">
                    {getNameSelectedLanguage(state.i18List)}
                  </StateColor>
                  <UncontrolledTooltip flip target={`tooltip-${state.id}`}>
                    {getNameSelectedLanguage(state.i18List)}
                  </UncontrolledTooltip>
                </td>
                <TdActions className="">
                  <ButtonIcon
                    type="button"
                    Icon={() => <EditIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={() => onEdit(state)}
                  />
                  <ButtonIcon
                    type="button"
                    onClick={() => onRemove(state)}
                    Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                    className="button-icon mr-n3 ml-2"
                  />
                </TdActions>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="d-flex justify-content-center mb-5">
          <Spinner color={mainColor} />
        </div>
      )}
      {states.length === 0 && (
        <div className="panel_control__empty-folder d-flex flex-column align-items-center">
          <TagIcon fill={colors.suvaGrey} width="71" />
          <span>No hay Estados</span>
        </div>
      )}
      {states.length > 0 && (
        <Pagination
          titlePagination={t('general_states')}
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={states.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmRemoveState}
        toggle={onCancelRemove}
        confirmMessage={t('custom_feild_delete_field_confirmation').replace(
          '{fieldName}',
          getNameSelectedLanguage(SelectedRemoveState?.i18List)
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={t('custom_field_delete_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_field_success').replace(
          '{fieldName}',
          getNameSelectedLanguage(SelectedRemoveState?.i18List)
        )}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
    </div>
  );
};

StatesTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  setCurrentState: PropTypes.func.isRequired,
  setOpenFormModal: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  urlFragment: PropTypes.string.isRequired,
};

export default StatesTable;
